import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import { PDFViewer, PDFDownloadLink } from "@react-pdf/renderer";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import BarcodePDF from "../components/BarcodePDF";
import { showProducts, isErpApiSuccess } from "../erpClient";
import { normalizeProductStockShowResponse } from "../utils/productStockErpAdapter";
import { getTenantNameDisplay } from "../utils/tenantName";

function PrintBarcode() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedType, setSelectedType] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [startPosition, setStartPosition] = useState(1);
  const [barcodeData, setBarcodeData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get selected items from navigation state
  useEffect(() => {
    if (location.state?.selectedItems) {
      setSelectedItems(location.state.selectedItems);
      setShowModal(true);
    } else {
      setShowModal(true);
    }
  }, [location.state]);

  // Listen for API URL changes - navigate back to stock table
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in PrintBarcode, navigating back...', event.detail);
      toast('Database switched. Returning to stock table.');
      navigate('/stocktable');
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);
    
    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [navigate]);

  // Fetch barcode data from API
  const fetchBarcodeData = async (items) => {
    if (!items || items.length === 0) return;

    setIsLoading(true);
    try {
      const barcodePromises = items.map(async (item) => {
        const productId = item.id || item.productId || item.tagNo;
        if (!productId) return null;

        try {
          const idNum = Number(productId);
          const response =
            Number.isFinite(idNum) && idNum > 0
              ? await showProducts({ id: idNum })
              : await showProducts({ tag_no: String(productId) });
          const prods = normalizeProductStockShowResponse(response);
          const p = prods[0];
          if (isErpApiSuccess(response) && p) {
            return {
              ...item,
              barcodeData: {
                product: {
                  ...item,
                  ...p,
                  gwt: p.gwt ?? p.GWT ?? item.gWt ?? item.gwt,
                  lwt_customer:
                    p.lwt_customer ??
                    p.LWt_customer ??
                    item.lWtCustomer ??
                    item.lWt,
                  nwt: p.nwt ?? p.NWT ?? item.nwt,
                  tag_no: p.tag_no ?? item.tagNo ?? item.tag_no,
                  supplier_name:
                    p.supplier_name ??
                    p.supplier_company_name ??
                    item.supplier,
                  short_code: p.short_code ?? item.shortCode,
                  charges:
                    Array.isArray(p.charges) && p.charges.length > 0
                      ? p.charges
                      : item.charges,
                },
              },
              productId: productId,
            };
          }
          console.warn(`No barcode data found for product ID: ${productId}`);
          return item;
        } catch (error) {
          console.error(
            `Error fetching barcode for product ${productId}:`,
            error,
          );
          return item;
        }
      });

      const results = await Promise.all(barcodePromises);
      const validResults = results.filter(Boolean);
      setBarcodeData(validResults);
    } catch (error) {
      console.error("Error fetching barcode data:", error);
      toast.error("Failed to fetch barcode data");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch barcode data when selectedItems and selectedType change
  useEffect(() => {
    if (selectedItems.length > 0 && selectedType) {
      fetchBarcodeData(selectedItems);
    }
  }, [selectedItems, selectedType]);

  const handleTypeSelection = (type) => {
    setSelectedType(type);
    setStartPosition(1);
    // Keep modal open to show position input
  };

  const handleProceed = () => {
    if (isLoading) {
      toast.error("Please wait — barcode data is still loading");
      return;
    }

    const maxPos = selectedType === "Silver" ? 10 : 9;
    const pos = Math.min(Math.max(1, Number(startPosition) || 1), maxPos);
    setStartPosition(pos);
    setShowModal(false);
    setShowPdfModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleOpenPdfModal = () => {
    setShowPdfModal(true);
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
  };


  const handlePrintClick = () => {
    setStartPosition(1);
    setShowModal(true);
  };

  // Memoize the PDF document to prevent re-rendering
  const pdfDocument = useMemo(() => {
    const dataToUse = barcodeData.length > 0 ? barcodeData : selectedItems;
    return (
      <BarcodePDF
        selectedItems={dataToUse}
        barcodeType={selectedType}
        startPosition={startPosition}
      />
    );
  }, [barcodeData, selectedItems, selectedType, startPosition]);

  return (
    <div className="body-wrapper">
      <div className="container-fluid">
        <div className="card w-100 position-relative overflow-hidden">
          <div className="px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
            <h4 className="card-title mb-0">Print Barcode</h4>
            {(selectedType === "Silver" || selectedType === "Gold") &&
              selectedItems.length > 0 && (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-secondary"
                  onClick={handlePrintClick}
                >
                  Print
                </button>
              </div>
            )}
          </div>
          <div className="card-body p-4">
            {isLoading && !showModal && !showPdfModal && (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">
                    Loading barcode data...
                  </span>
                </div>
                <span className="ms-2">Loading barcode data...</span>
              </div>
            )}

            {/* Barcode Preview - Single barcode with field names only */}
            {/* {(selectedType === "Silver" || selectedType === "Gold") && !isLoading && !showModal && (
              <div className="d-flex justify-content-center align-items-center py-4">
                <div
                  className="border border-2 shadow-sm d-flex"
                  style={{
                    width: "165mm",
                    height: selectedType === "Gold" ? "54mm" : "42mm",
                    minWidth: "165mm",
                    minHeight: selectedType === "Gold" ? "45mm" : "42mm",
                    boxSizing: "border-box",
                    overflow: "visible",
                    borderColor: "#6c757d",
                    borderRadius: "6px",
                    transform: "scale(1)",
                  }}
                >
                  {/* Left Half - Barcode Area */}
                  {/* <div
                    className="d-flex flex-column justify-content-start"
                    style={{
                      width: "82.5mm",
                      height: "100%",
                      fontSize: selectedType === "Gold" ? "21px" : "18px",
                      fontWeight: "500",
                      color: "#000",
                      backgroundColor: "#f8f9fa",
                      borderTopLeftRadius: "6px",
                      borderBottomLeftRadius: "6px",
                      overflow: "hidden",
                      padding: "6px",
                      paddingTop: selectedType === "Gold" ? "12px" : "6px",
                      lineHeight: "1.1",
                    }}
                  >
                    {selectedType === "Gold" && (
                      <div
                        style={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          textAlign: "center",
                          marginBottom: "3px",
                          border: "1px dashed rgb(185, 73, 69)",
                          padding: "2px 4px",
                          display: "inline-block",
                        }}
                      >
                        MANGLIK
                      </div>
                    )}

                    {/* Barcode placeholder */}
                    {/* <div
                      className="d-flex justify-content-center align-items-center"
                      style={{
                        height: selectedType === "Gold" ? "30px" : "24px",
                        marginBottom: "3px",
                        borderRadius: "2px",
                        border: "1px dashed rgb(185, 73, 69)",
                      }}
                    >
                      <strong style={{ fontSize: "17px" }}>BARCODE</strong>
                    </div>

                    {/* Tag no (left) | LBR% (right) */}
                    {/* <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "3px",
                        fontSize: selectedType === "Gold" ? "18px" : "15px",
                        fontWeight: "bold",
                      }}
                    >
                      <strong style={{ textAlign: "left", border: "1px dashed rgb(185, 73, 69)", padding: "2px 4px" }}>Tag No</strong>
                      <strong style={{ textAlign: "right", border: "1px dashed rgb(185, 73, 69)", padding: "2px 4px" }}>LBR%</strong>
                    </div>

                    {selectedType === "Gold" ? (
                      /* Gold: Charges */}
                      {/* <div style={{ fontSize: "15px", textAlign: "center" }}>
                        <strong style={{ marginBottom: "1.5px", border: "1px dashed rgb(185, 73, 69)", padding: "2px 4px", display: "inline-block" }}>Other Charges</strong>
                      </div>
                    ) : (
                      /* Silver: short_code and size */}
                      {/* <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "19.5px",
                          fontWeight: "bold",
                        }}
                      >
                        <strong style={{ textAlign: "left", border: "1px dashed rgb(185, 73, 69)", padding: "2px 4px" }}>
                          <strong>Short</strong>
                          <strong>Code</strong>
                        </strong>
                        <strong style={{ textAlign: "right", border: "1px dashed rgb(185, 73, 69)", padding: "2px 4px" }}>Size</strong>
                      </div>
                    )}
                  </div>

                  {/* Black Partition Line */}
                  {/* <div
                    style={{
                      width: "3px",
                      height: "100%",
                      backgroundColor: "#000000",
                    }}
                  ></div>

                  {/* Right Half - Data Area */}
                  {/* <div
                    className="d-flex flex-column justify-content-start align-items-center"
                    style={{
                      width: "82.5mm",
                      height: "100%",
                      fontSize: selectedType === "Gold" ? "18px" : "15px",
                      fontWeight: "500",
                      color: "#000",
                      backgroundColor: "#f8f9fa",
                      borderTopRightRadius: "6px",
                      borderBottomRightRadius: "6px",
                      padding: "6px",
                      paddingTop: selectedType === "Gold" ? "12px" : "6px",
                      lineHeight: "1.2",
                    }}
                  >
                    {selectedType === "Gold" ? (
                      /* Gold layout - consistent box sizes */}
                      {/* <div style={{  margin: "0 auto"}}>
                        <div
                          style={{
                            marginBottom: "3px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "stretch",
                            gap: "6px",
                          }}
                        >
                          <strong style={{ 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px", 
                            width: "50px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px"
                          }}>GW</strong>
                          <strong style={{ 
                            width: "100px", 
                            textAlign: "left", 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "14px"
                          }}>GW Value</strong>
                        </div>
                        <div
                          style={{
                            marginBottom: "3px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "stretch",
                            gap: "6px",
                          }}
                        >
                          <strong style={{ 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            width: "50px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px"
                          }}>LW</strong>
                          <strong style={{ 
                            width: "100px", 
                            textAlign: "right", 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            fontSize: "14px"
                          }}>LW Value</strong>
                        </div>
                        <div
                          style={{
                            marginBottom: "3px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "stretch",
                            gap: "6px",
                          }}
                        >
                          <strong style={{ 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            width: "50px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px"
                          }}>NW</strong>
                          <strong style={{ 
                            width: "100px", 
                            textAlign: "right", 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "left",
                            fontSize: "14px"
                          }}>NW Value</strong>
                        </div>
                        <div
                          style={{
                            marginBottom: "3px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "stretch",
                            gap: "6px",
                          }}
                        >
                          <strong style={{ 
                             border: "1px dashed rgb(185, 73, 69)", 
                             padding: "2px 4px",
                             width: "50px",
                             height: "24px",
                             display: "flex",
                             alignItems: "center",
                             justifyContent: "center",
                             fontSize: "8px",
                             lineHeight: "1.1",
                             textAlign: "center",
                             flexDirection: "column"
                          }}>
                            <strong>Short</strong>
                            <strong>Code</strong>
                          </strong>
                          <strong style={{ 
                            width: "100px", 
                            textAlign: "left", 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "left",
                            fontSize: "14px"
                          }}>Purity</strong>
                        </div>
                        <div
                          style={{
                            marginBottom: "3px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "stretch",
                            gap: "6px",
                          }}
                        >
                          <strong style={{ 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            width: "50px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "8px",
                            lineHeight: "1.1",
                            textAlign: "center",
                            flexDirection: "column"
                          }}>
                            <strong>Supplier</strong>
                            <strong>Name</strong>
                          </strong>
                          <strong style={{ 
                            width: "100px", 
                            textAlign: "right", 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "left",
                            fontSize: "14px"
                          }}>Size</strong>
                        </div>
                        {/* HUID - Gold only, centered below the data */}
                        {/* <div
                          style={{
                            width: "100%",
                            marginTop: "6px",
                            textAlign: "center",
                          }}
                        >
                          <strong
                            style={{
                              fontSize: "15px",
                              border: "1px dashed rgb(185, 73, 69)",
                              padding: "2px 4px",
                              display: "inline-block",
                            }}
                          >
                            HUID NO
                          </strong>
                        </div>
                      </div>
                    ) : (
                      /* Silver layout - consistent box sizes */}
                      {/* <div
                        style={{
                          alignSelf: "center",
                          alignItems: "center",
                          width: "156px",
                        }}
                      >
                        <div
                          style={{
                            width: "100%",
                            marginTop: "2px",
                            textAlign: "center",
                            marginBottom: "6px",
                          }}
                        >
                          <strong
                            style={{
                              fontSize: "15px",
                              border: "1px dashed rgb(185, 73, 69)",
                              padding: "2px 4px",
                              display: "inline-block",
                            }}
                          >
                            {getTenantNameDisplay()}
                          </strong>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "stretch",
                            marginBottom: "3px",
                            gap: "6px",
                          }}
                        >
                          <strong style={{ 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            width: "50px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px"
                          }}>GW</strong>
                          <strong style={{ 
                            width: "100px", 
                            textAlign: "left", 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            fontSize: "14px"
                          }}>GW Value</strong>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "stretch",
                            marginBottom: "3px",
                            gap: "6px",
                          }}
                        >
                          <strong style={{ 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            width: "50px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "14px"
                          }}>NW</strong>
                          <strong style={{ 
                            width: "100px", 
                            textAlign: "left", 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            fontSize: "14px"
                          }}>NW Value</strong>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "stretch",
                            gap: "6px",
                          }}
                        >
                          <strong style={{ 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            width: "50px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "8px",
                            lineHeight: "1.1",
                            textAlign: "center",
                            flexDirection: "column"
                          }}>
                            <strong>Supplier</strong>
                            <strong>Name</strong>
                          </strong>
                          <strong style={{ 
                            width: "100px", 
                            textAlign: "left", 
                            border: "1px dashed rgb(185, 73, 69)", 
                            padding: "2px 4px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                            fontSize: "14px"
                          }}>Size</strong>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Barcode Type Selection Modal */}
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Select Barcode Type</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleModalClose}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <p className="mb-3">
                  Which type of barcode would you like to print?
                </p>
                <div className="form-check mb-3">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="barcodeType"
                    id="gold"
                    value="Gold"
                    checked={selectedType === "Gold"}
                    onChange={(e) => handleTypeSelection(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="gold">
                    <strong>Gold sheet</strong>
                  </label>
                </div>
                <div className="form-check mb-4">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="barcodeType"
                    id="silver"
                    value="Silver"
                    checked={selectedType === "Silver"}
                    onChange={(e) => handleTypeSelection(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="silver">
                    <strong>Silver Sheet</strong>
                  </label>
                </div>

                {/* Show position input only after type is selected */}
                {(selectedType === "Gold" || selectedType === "Silver") && (
                  <div className="mb-3">
                    <label htmlFor="startPosition" className="form-label">
                      Start printing from position
                    </label>
                    <input
                      id="startPosition"
                      type="number"
                      placeholder="Enter position number"
                      min="1"
                      max={selectedType === "Silver" ? 10 : 9}
                      className="form-control"
                      value={startPosition}
                      onChange={(e) => setStartPosition(e.target.value)}
                    />
                  </div>
                )}
              </div>
              <div className="modal-footer">
                {(selectedType === "Gold" || selectedType === "Silver") && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleProceed}
                    disabled={isLoading}
                  >
                    {isLoading ? "Loading..." : "Proceed"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Barcode PDF Modal */}
      <Modal
        show={showPdfModal}
        onHide={handleClosePdfModal}
        size="xl"
        centered
        dialogClassName="pdf-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <Icon icon="solar:barcode-bold" className="me-2" />
            Barcode Sheet Preview - {selectedType}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", overflow: "auto" }}>
          <PDFViewer style={{ width: "100%", height: "100%" }}>
            {pdfDocument}
          </PDFViewer>
        </Modal.Body>
        <Modal.Footer>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default PrintBarcode;
